import dayjs from 'dayjs';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { updateRequestSamplesForm } from 'Apollo/Store/Showroom/UserView/Samples/Form/resolvers';
import { OccasionType, useGetShowroomSamplesFormQuery } from 'generated/graphql';

import { useDateLocale } from 'lib/Hooks';

import { MenuItem } from '@material-ui/core';
import Divider from '@material-ui/core/Divider';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';

import DatePicker from 'components/Common/DatePicker';

import { useStyles } from './styles';

const ShowroomSamplesFormStepsOptional: React.FC = () => {
	const classes = useStyles();
	const { t, i18n } = useTranslation();
	const dateLocale = useDateLocale(i18n.language);

	const {
		data: {
			showroom: {
				userView: {
					samples: {
						form: {
							occasionType,
							runThroughDate,
							publication,
							storyTheme,
							eventName,
							celebrity,
							additionalComments,
							shootDateStart,
							shootDateEnd,
							shootLocation,
							photographer,
							modelName
						}
					}
				}
			}
		}
	} = useGetShowroomSamplesFormQuery();

	const additionalTextFieldInput = React.useMemo(() => {
		return (
			<TextField
				id="additionalCommentsInput"
				defaultValue={additionalComments}
				onChange={ev => updateRequestSamplesForm({ additionalComments: ev.target.value })}
				label={t('showroom.samples.form.additionalComments')}
				size="small"
				className={classes.optionalInput}
				variant="outlined"
				multiline
				placeholder={t(`showroom.samples.form.${occasionType.toLowerCase()}.addionalComments`)}
				fullWidth
				rows={4}
			/>
		);
	}, [additionalComments, classes.optionalInput, occasionType, t]);

	const runThroughDateInput = React.useMemo(() => {
		return (
			<DatePicker
				id="runThroughDateInput"
				cancelLabel={t(`showroom.samples.form.cancel`)}
				value={runThroughDate}
				onChange={date => updateRequestSamplesForm({ runThroughDate: date })}
				inputClassName={classes.optionalInput}
				inputLabel={t(`showroom.samples.form.${occasionType.toLowerCase()}.runThroughDate`)}
				disablePast={true}
				nullable
				minDate={dayjs(new Date()).add(1, 'days')}
				locale={dateLocale}
			/>
		);
	}, [classes.optionalInput, dateLocale, occasionType, runThroughDate, t]);

	const celebrityEventInputs = React.useMemo(
		() => (
			<>
				{runThroughDateInput}
				<TextField
					id="eventNameInput"
					defaultValue={eventName}
					onChange={ev => updateRequestSamplesForm({ eventName: ev.target.value })}
					size="small"
					className={classes.optionalInput}
					variant="outlined"
					label={t('showroom.samples.form.celebrity.eventName')}
					fullWidth
				/>
				<TextField
					id="celebrityInput"
					defaultValue={celebrity}
					onChange={ev => updateRequestSamplesForm({ celebrity: ev.target.value })}
					size="small"
					className={classes.optionalInput}
					variant="outlined"
					label={t('showroom.samples.form.celebrity.celebrity')}
					fullWidth
				/>
				<TextField
					id="shootLocationInput"
					defaultValue={shootLocation}
					onChange={ev => updateRequestSamplesForm({ shootLocation: ev.target.value })}
					size="small"
					className={classes.optionalInput}
					variant="outlined"
					label={t('showroom.samples.form.celebrity.shootLocation')}
					fullWidth
				/>
				{additionalTextFieldInput}
			</>
		),
		[additionalTextFieldInput, celebrity, classes.optionalInput, eventName, runThroughDateInput, shootLocation, t]
	);

	const otherInputs = React.useMemo(
		() => (
			<>
				{runThroughDateInput}
				{additionalTextFieldInput}
			</>
		),
		[additionalTextFieldInput, runThroughDateInput]
	);

	const photoShootInputs = React.useMemo(
		() => (
			<>
				{runThroughDateInput}
				<TextField
					id="publicationInput"
					defaultValue={publication}
					onChange={ev => updateRequestSamplesForm({ publication: ev.target.value })}
					size="small"
					className={classes.optionalInput}
					variant="outlined"
					label={t('showroom.samples.form.photo.publication')}
					placeholder="Ex: Vogue"
					fullWidth
				/>
				<div className={classes.optionalDateContainer}>
					<DatePicker
						id="shootDateStartInput"
						cancelLabel={t('showroom.samples.form.cancel')}
						value={shootDateStart}
						nullable
						onChange={date => {
							updateRequestSamplesForm({ shootDateStart: date });
						}}
						inputClassName={classes.optionalInputStartDate}
						inputLabel={t('showroom.samples.form.photo.shootDateStart')}
						disablePast={true}
						locale={dateLocale}
					/>
					<DatePicker
						id="shootDateEndInput"
						cancelLabel={t('showroom.samples.form.cancel')}
						value={shootDateEnd}
						nullable
						minDate={shootDateStart}
						onChange={date => updateRequestSamplesForm({ shootDateEnd: date })}
						inputClassName={classes.optionalInputEndDate}
						inputLabel={t('showroom.samples.form.photo.shootDateEnd')}
						disablePast={true}
						locale={dateLocale}
					/>
				</div>
				<TextField
					id="shootLocationInput"
					defaultValue={shootLocation}
					onChange={ev => updateRequestSamplesForm({ shootLocation: ev.target.value })}
					size="small"
					className={classes.optionalInput}
					variant="outlined"
					label={t('showroom.samples.form.photo.shootLocation')}
					fullWidth
				/>
				<TextField
					id="storyThemeInput"
					defaultValue={storyTheme}
					onChange={ev => updateRequestSamplesForm({ storyTheme: ev.target.value })}
					size="small"
					className={classes.optionalInput}
					variant="outlined"
					label={t('showroom.samples.form.photo.storyTheme')}
					fullWidth
				/>
				<TextField
					id="modelNameInput"
					defaultValue={modelName}
					onChange={ev => updateRequestSamplesForm({ modelName: ev.target.value })}
					size="small"
					className={classes.optionalInput}
					variant="outlined"
					label={t('showroom.samples.form.photo.modelName')}
					fullWidth
				/>
				<TextField
					id="photographerInput"
					defaultValue={photographer}
					onChange={ev => updateRequestSamplesForm({ photographer: ev.target.value })}
					size="small"
					className={classes.optionalInput}
					variant="outlined"
					label={t('showroom.samples.form.photo.photographer')}
					fullWidth
				/>
				{additionalTextFieldInput}
			</>
		),
		[
			runThroughDateInput,
			publication,
			classes.optionalInput,
			classes.optionalDateContainer,
			classes.optionalInputStartDate,
			classes.optionalInputEndDate,
			t,
			shootDateStart,
			dateLocale,
			shootDateEnd,
			shootLocation,
			storyTheme,
			modelName,
			photographer,
			additionalTextFieldInput
		]
	);

	const occasionTypeFields = React.useMemo(() => {
		switch (occasionType) {
			case OccasionType.Celebrity:
				return celebrityEventInputs;
			case OccasionType.Photo:
				return photoShootInputs;
			case OccasionType.Other:
				return otherInputs;
			default:
				return null;
		}
	}, [celebrityEventInputs, occasionType, otherInputs, photoShootInputs]);

	const sortedTyleForms = React.useMemo(() => {
		return [OccasionType.None, OccasionType.Photo, OccasionType.Celebrity, OccasionType.Other];
	}, []);

	return (
		<div className={classes.root}>
			<div className={classes.header}>{t('showroom.samples.form.occasionInfo')}</div>
			<div>
				<div className={classes.optionalTitle}>{t('showroom.samples.form.optionalInfo')}</div>
				<div className={classes.optionalSelectorBox}>
					<Select
						id="occasionTypePhotoInput"
						onChange={ev => updateRequestSamplesForm({ occasionType: ev.target.value as OccasionType })}
						defaultValue={occasionType}
						fullWidth
						variant={'outlined'}
					>
						{sortedTyleForms.map(value => (
							<MenuItem value={value} key={`sampleRequestLastStepFormMenuItem-${value}`}>
								{t(`showroom.samples.form.occasion.${value.toLowerCase()}`)}
							</MenuItem>
						))}
					</Select>
				</div>
				<Divider variant="middle" className={classes.optionalBoxDivider} />
				{occasionTypeFields}
			</div>
		</div>
	);
};

export default React.memo(ShowroomSamplesFormStepsOptional);
